import type { ApolloError } from "@apollo/client";
import type { AxiosError } from "axios";
import { AlertCircle, AlertTriangle, Loader2, Wrench, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { SuperAdminOnly } from "@/components/shared/miscellaneous/superadmin-only";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ErrorMessage } from "@/components/ui/error";
import { Link } from "@/components/ui/link";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { useTenant } from "@/providers/session";

enum ErrorCode {
  DATEV = "datev",
  UNKNOWN = "unknown",
}

type BankBookingExportMonthSelectionProps = {
  bankTransactionIds: string[];
  bankTransactionsExportGroups: Record<string, string[]>;
  onConfirm: (months: Record<string, string[]>) => Promise<void>;
  onGenerateDatevFormatFiles: (
    months: Record<string, string[]>,
  ) => Promise<void>;
  resetRowSelection: () => void;
  totalBankTransactions: number;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
  hasMissingDates: boolean | string[];
  token: any | undefined;
};

export const BankBookingExportMonthSelection = ({
  bankTransactionIds,
  bankTransactionsExportGroups,
  onConfirm,
  onGenerateDatevFormatFiles,
  resetRowSelection,
  totalBankTransactions,
  loading,
  error,
  refetch,
  hasMissingDates,
  token,
}: BankBookingExportMonthSelectionProps) => {
  const { t } = useTranslation();
  const { companySlug } = useTenant();

  const [isDevToolsOpen, setIsDevToolsOpen] = useState(false);
  const [exportError, setExportError] = useState<ErrorCode | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);

  const months = useMemo(() => {
    return Object.keys(bankTransactionsExportGroups);
  }, [bankTransactionsExportGroups]);

  useEffect(() => {
    if (months.length > 0 && selectedMonths.length === 0) {
      setSelectedMonths(months);
    }
  }, [months, selectedMonths]);

  useEffect(() => {
    if (months.length > 0) {
      setSelectedMonths(months);
    }
  }, [bankTransactionIds, months]);

  const selectedBankTransactions = useMemo(() => {
    return selectedMonths.flatMap(
      (month) => bankTransactionsExportGroups[month],
    );
  }, [selectedMonths, bankTransactionsExportGroups]);

  const handleMonthSelection = (months: string[], isSelected: boolean) => {
    if (isSelected) {
      setSelectedMonths((prevMonths) => [
        ...prevMonths.filter((m) => !months.includes(m)),
        ...months,
      ]);
    } else {
      setSelectedMonths((prevMonths) =>
        prevMonths.filter((m) => !months.includes(m)),
      );
    }
  };

  const handleConfirm = async () => {
    const months = Object.fromEntries(
      selectedMonths.map((month) => [
        month,
        bankTransactionsExportGroups[month],
      ]),
    );
    await onConfirm(months);
  };

  const handleConfirmGenerateDatevFormatFiles = async () => {
    setIsExporting(true);

    try {
      await onGenerateDatevFormatFiles(
        Object.fromEntries(
          selectedMonths.map((month) => [
            month,
            bankTransactionsExportGroups[month],
          ]),
        ),
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    handleMonthSelection(months, checked);
  };

  const handleMonthSelect = (month: string, isSelected: boolean) => {
    handleMonthSelection([month], isSelected);
  };

  const checkedState = useMemo(() => {
    if (selectedMonths.length === 0) return false;
    if (selectedMonths.length === months.length) return true;
    return "indeterminate";
  }, [selectedMonths, months]);

  const tokenIssues = useMemo(() => {
    return (
      new Date(token?.refreshTokenExpirationDate) < new Date() || token?.error
    );
  }, [token]);

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6">
        <div className="flex flex-col gap-4 py-4">
          {tokenIssues && (
            <Alert variant="warning">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("exports.token_issues.title")}</AlertTitle>
              <AlertDescription className="whitespace-pre-line">
                {t("exports.token_issues.description.title")}
              </AlertDescription>
              <ul className="list-decimal ml-2">
                <li>
                  <Trans
                    i18nKey={"exports.token_issues.description.step_1"}
                    components={{
                      Link: (
                        <Link
                          className="underline font-semibold"
                          href={{
                            pathname:
                              "/$company/buchhaltung/einstellungen/integrationen",
                            query: { company: companySlug },
                          }}
                        />
                      ),
                    }}
                  />
                </li>
                <li>{t("exports.token_issues.description.step_2")}</li>
                <li>{t("exports.token_issues.description.step_3")}</li>
              </ul>
            </Alert>
          )}
          {exportError === ErrorCode.DATEV && (
            <Alert variant="warning">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("exports.datev_errors.title")}</AlertTitle>
              <AlertDescription className="whitespace-pre-line">
                {t("exports.datev_errors.description")}
              </AlertDescription>
            </Alert>
          )}
          {hasMissingDates && (
            <Alert variant="warning">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("exports.missing_dates.title")}</AlertTitle>
              <AlertDescription className="whitespace-pre-line">
                {t("exports.missing_dates.description")}
              </AlertDescription>
            </Alert>
          )}
          <DialogContent
            months={months}
            checkedState={checkedState}
            handleSelectAll={handleSelectAll}
            handleMonthSelect={handleMonthSelect}
            groups={bankTransactionsExportGroups}
            selectedMonths={selectedMonths}
            loading={loading}
            error={error}
            refetch={refetch}
          />
        </div>
      </div>

      <div className="px-6 pb-4">
        <SuperAdminOnly>
          <Collapsible open={isDevToolsOpen} onOpenChange={setIsDevToolsOpen}>
            <div className="border border-amber-200 bg-amber-50 rounded-lg overflow-hidden shadow-sm">
              <CollapsibleTrigger className="w-full">
                <div className="flex items-center justify-between px-4 py-2 hover:bg-amber-100 transition-colors cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-900">
                      Super Admin Tools
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isDevToolsOpen ? (
                      <span className="text-xs text-amber-600">Hide</span>
                    ) : (
                      <span className="text-xs text-amber-600">Show tools</span>
                    )}
                  </div>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="border-t border-amber-200 bg-white px-4 py-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                    </div>
                    <div className="flex-1 space-y-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-1">
                          Developer Tools
                        </h4>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          These tools are for development and testing purposes
                          only.
                        </p>
                      </div>
                      <div className="pt-1">
                        <Button
                          onClick={handleConfirmGenerateDatevFormatFiles}
                          disabled={isExporting || loading}
                          variant="outline"
                          size="sm"
                          className="gap-1"
                        >
                          {isExporting && (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          )}
                          {`DATEV-Format (${Object.values(bankTransactionsExportGroups).flat().length})`}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        </SuperAdminOnly>
      </div>

      <div className="border-t px-6 py-2">
        <div className="flex items-center gap-2">
          <ExportButton
            onConfirm={handleConfirm}
            selectedBankTransactions={bankTransactionIds}
            selectedBankTransactionsToExport={selectedBankTransactions}
            resetRowSelection={resetRowSelection}
            tokenIssues={tokenIssues}
            setExportError={setExportError}
            totalBankTransactions={totalBankTransactions}
          />
        </div>
      </div>
    </>
  );
};

const DialogContent = ({
  months,
  checkedState,
  handleSelectAll,
  handleMonthSelect,
  groups,
  selectedMonths,
  loading,
  error,
  refetch,
}: {
  months: string[];
  checkedState: boolean | "indeterminate";
  handleSelectAll: (checked: boolean) => void;
  handleMonthSelect: (month: string, isSelected: boolean) => void;
  groups: Record<string, string[]>;
  selectedMonths: string[];
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
}) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] =
    useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    const checkScrollable = () => {
      if (scrollAreaRef.current) {
        const { scrollHeight, clientHeight } = scrollAreaRef.current;
        setShowScrollIndicator(scrollHeight > clientHeight + 10);
      }
    };

    checkScrollable();
    const timeoutId = setTimeout(checkScrollable, 100);

    return () => clearTimeout(timeoutId);
  }, [loading, months]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    setShowScrollIndicator(scrollTop + clientHeight < scrollHeight - 10);
  };

  if (error) {
    return (
      <div className="flex justify-center items-center h-[200px]">
        <ErrorMessage retry={refetch} />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col mt-2 -mb-2">
        <p className="text-lg font-semibold">
          {t("exports.month_selection_dialog.title")}
        </p>
        <p className="text-sm text-gray-500">
          {t("exports.month_selection_dialog.description")}
        </p>
      </div>
      <div className="relative">
        <ScrollArea
          className="max-h-[30vh] overflow-y-auto"
          onScroll={handleScroll}
          ref={scrollAreaRef}
        >
          {loading ? (
            <LoadingSkeleton />
          ) : (
            <div className="flex flex-col gap-2 pb-2">
              {months.length > 1 && (
                <label className="flex items-center space-x-2">
                  <Checkbox
                    checked={checkedState}
                    onCheckedChange={(checked) => handleSelectAll(!!checked)}
                  />
                  <span>{t("exports.month_selection_dialog.select_all")}</span>
                </label>
              )}
              {months
                .sort((a, b) => {
                  if (a === "not_booked") return 1;
                  if (b === "not_booked") return -1;
                  const [yearA, monthA] = a.split("-").map(Number);
                  const [yearB, monthB] = b.split("-").map(Number);
                  if (yearA !== yearB) return yearB - yearA;
                  return monthB - monthA;
                })
                .map((month) => {
                  const [year, monthNum] = month.split("-");
                  const date = new Date(
                    Number.parseInt(year),
                    Number.parseInt(monthNum) - 1,
                  );
                  const formattedMonth = new Intl.DateTimeFormat("de-DE", {
                    month: "short",
                    year: "numeric",
                  }).format(date);

                  const monthInvoices = groups[month] || [];

                  return (
                    <label
                      key={month}
                      className="flex items-center space-x-2 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedMonths.includes(month)}
                        onCheckedChange={(checked) =>
                          handleMonthSelect(month, !!checked)
                        }
                      />
                      <span>{formattedMonth}</span>
                      <span className="text-gray-500 text-sm">
                        ({monthInvoices.length}{" "}
                        {monthInvoices.length === 1
                          ? "Transaktion"
                          : "Transaktionen"}
                        )
                      </span>
                    </label>
                  );
                })}
            </div>
          )}
        </ScrollArea>
        {showScrollIndicator && (
          <div className="absolute bottom-0 left-0 right-0 text-center py-2 bg-gradient-to-t from-white via-white/90 to-transparent">
            <span className="text-sm text-gray-500">
              ↓ {t("exports.month_selection_dialog.scroll_for_more")}
            </span>
          </div>
        )}
      </div>
    </>
  );
};

const LoadingSkeleton = () => {
  return (
    <div className="flex flex-col gap-2 py-2">
      <div className="flex items-center space-x-2">
        <Skeleton className="h-4 w-4 rounded flex-shrink-0" />
        <Skeleton className="h-4 flex-1" />
      </div>

      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex items-center space-x-2">
          <Skeleton className="h-4 w-4 rounded flex-shrink-0" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 flex-1 opacity-60" />
        </div>
      ))}
    </div>
  );
};

const ExportButton = ({
  tokenIssues,
  onConfirm,
  selectedBankTransactions,
  selectedBankTransactionsToExport,
  resetRowSelection,
  setExportError,
  totalBankTransactions,
}: {
  onConfirm: () => Promise<void>;
  selectedBankTransactions: string[];
  selectedBankTransactionsToExport: string[];
  resetRowSelection: () => void;
  tokenIssues: boolean;
  setExportError: (error: ErrorCode) => void;
  totalBankTransactions: number;
}) => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const handleConfirm = async () => {
    setLoading(true);

    try {
      await onConfirm();
    } catch (_error) {
      const error = _error as AxiosError<{ code: string; message: string }>;
      const errorData = error.response?.data;

      if (errorData?.code?.includes("EDI")) {
        setExportError(ErrorCode.DATEV);
        return;
      }
      setExportError(ErrorCode.UNKNOWN);
    } finally {
      setLoading(false);
    }
  };

  const exportAll = useMemo(() => {
    if (selectedBankTransactions.length > 0) {
      return false;
    }

    return true;
  }, [selectedBankTransactions]);

  const count = useMemo(() => {
    if (exportAll) {
      return totalBankTransactions;
    }
    return selectedBankTransactions.length;
  }, [exportAll, totalBankTransactions, selectedBankTransactions]);

  const countDisplay = useMemo(() => {
    if (selectedBankTransactions.length > 0) {
      return {
        number: `${selectedBankTransactions.length} von ${totalBankTransactions}`,
        label: t("transactions.exports.selected_label"),
      };
    }

    return {
      number: count.toString(),
      label: t("transactions.exports.label"),
    };
  }, [count, selectedBankTransactions.length, totalBankTransactions, t]);

  const buttonLabel = useMemo(() => {
    if (selectedBankTransactionsToExport.length > 0) {
      return t("exports.month_selection_dialog.export", {
        count: selectedBankTransactionsToExport.length,
      });
    }
    return t("export_all_button");
  }, [selectedBankTransactionsToExport.length, t]);

  const handleClearSelection = () => {
    resetRowSelection();
  };

  return (
    <div className="flex flex-1 w-full justify-end">
      <div className="flex items-center gap-2 px-[11px] py-[7px]">
        {selectedBankTransactions.length > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 -m-1 hover:bg-secondary/50"
            onClick={handleClearSelection}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
        <div className="flex flex-col items-center py-0.5">
          <span className="text-lg font-semibold leading-none">
            {countDisplay.number}
          </span>
          <span className="text-[11px] text-muted-foreground leading-tight mt-0.5">
            {countDisplay.label}
          </span>
        </div>
        <div className="h-6 w-px bg-border" />
        <Button
          onClick={handleConfirm}
          disabled={tokenIssues}
          className="gap-1 flex-1"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
};
